import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const KEY = "fabj_currency";
const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState("USD");
  const [rate, setRate] = useState(1);
  const [symbol, setSymbol] = useState("$");
  const [available, setAvailable] = useState({ USD: { rate: 1, symbol: "$" } });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const boot = async () => {
      try {
        const [{ data: geo }, { data: fx }] = await Promise.all([
          api.get("/geo"),
          api.get("/fx"),
        ]);
        setAvailable(fx.currencies || { USD: { rate: 1, symbol: "$" } });
        const saved = localStorage.getItem(KEY);
        const chosen = saved && (fx.currencies || {})[saved] ? saved : geo.currency;
        applyCurrency(chosen, fx.currencies || { [chosen]: { rate: geo.rate_from_usd, symbol: geo.symbol } });
      } catch (err) {
        console.error("Currency detection failed, defaulting to USD", err);
      } finally { setReady(true); }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCurrency = (code, fxMap) => {
    const info = (fxMap || available)[code];
    if (!info) return;
    setCurrency(code);
    setRate(info.rate || 1);
    setSymbol(info.symbol || code);
    localStorage.setItem(KEY, code);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- applyCurrency is a stable in-component helper
  const change = useCallback((code) => applyCurrency(code, available), [available]);

  const format = useCallback((usd) => {
    const v = (Number(usd) || 0) * (rate || 1);
    const hasCents = Math.round(v * 100) % 100 !== 0 || currency !== "USD";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: currency === "JPY" ? 0 : (hasCents ? 2 : 0),
        maximumFractionDigits: currency === "JPY" ? 0 : 2,
      }).format(v);
    } catch {
      return `${symbol}${v.toFixed(2)}`;
    }
  }, [currency, rate, symbol]);

  const value = useMemo(() => ({ currency, rate, symbol, available, change, format, ready }), [currency, rate, symbol, available, change, format, ready]);
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
export const useMoney = () => {
  const ctx = useContext(CurrencyContext);
  return ctx?.format || ((usd) => `$${(Number(usd) || 0).toFixed(2)}`);
};
